import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  useParams,
  useNavigate,
  useLocation,
} from 'react-router-dom';

import Header from './components/Header';
import Footer from './components/Footer';
import AddToCartPopup from './components/AddToCartPopup';

import HomePage from './pages/HomePage';
import CategoryPage from './pages/CategoryPage';
import ProductDetailPage from './pages/ProductDetailPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import WarrantyPolicyPage from './pages/WarrantyPolicyPage';
import SearchPage from './pages/SearchPage';
import CartPage from './pages/CartPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import Registro from './pages/Registro';
import LoginPage from './pages/Login';
import MisPedidosPage from './pages/MisPedidos';
import MiCuenta from './pages/MiCuenta';
import ResetPassword from './pages/ResetPassword';
import TilopayReturnPage from './pages/TilopayReturnPage';
import WhatsAppWidget from "./components/WhatsAppWidget.js";
import { routes } from './routes';
import { supabase } from './supabaseClient';
import { sendUniversalEmail } from "./utils/mailer";
import { Helmet } from 'react-helmet-async';
import { useBranding } from "./context/BrandingContext";

import AOS from 'aos';
import 'aos/dist/aos.css';

/* ---------------- Toast con fallback inline ----------------- */
function Toast({ toast, onClose }) {
  if (!toast) return null;

  const { branding } = useBranding();
  const brandColor = branding.colors.primary;

  const theme = {
    success: { barClass: "bg-green-500", barColor: "#22c55e", textClass: "text-green-700", textColor: "#15803d", ringClass: "ring-green-200" },
    error:   { barClass: "bg-red-500",   barColor: "#ef4444", textClass: "text-red-700",   textColor: "#b91c1c", ringClass: "ring-red-200"   },
    warning: { barClass: "bg-amber-500", barColor: "#f59e0b", textClass: "text-amber-700", textColor: "#b45309", ringClass: "ring-amber-200" },
    info:    { barClass: "bg-brand", barColor: brandColor, textClass: "text-brand", textColor: brandColor, ringClass: "ring-brand/30" },
  }[toast.variant || "info"];

  return (
    <div
      className="fixed top-4 right-4 z-[9999] w-[92vw] max-w-sm pointer-events-none"
      style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, width: '92vw', maxWidth: '24rem', pointerEvents: 'none' }}
    >
      <div
        role="status"
        aria-live="polite"
        className={`pointer-events-auto shadow-xl rounded-xl border border-gray-100 bg-white overflow-hidden ring-1 ${theme.ringClass} animate-[fadeIn_.18s_ease-out]`}
        style={{
          pointerEvents: 'auto',
          background: '#fff',
          border: '1px solid #eee',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.08)'
        }}
      >
        <div className="flex" style={{ display: 'flex' }}>
          <div className={`${theme.barClass} w-1`} style={{ backgroundColor: theme.barColor, width: 4 }} />
          <div className="flex-1 p-3" style={{ flex: 1, padding: 12 }}>
            <div className="flex items-start" style={{ display: 'flex', alignItems: 'flex-start' }}>
              <div className={`mr-2 mt-0.5 ${theme.textClass}`} style={{ color: theme.textColor, marginRight: 8, marginTop: 2 }}>
                <strong>{toast.variant === 'error' ? '⚠' : toast.variant === 'warning' ? '!' : toast.variant === 'success' ? '✓' : 'i'}</strong>
              </div>
              <div className="flex-1" style={{ flex: 1 }}>
                {toast.title && <p className="text-sm font-semibold text-gray-900" style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827' }}>{toast.title}</p>}
                {toast.description && (
                  <p
                    className="text-sm text-gray-600 mt-0.5"
                    style={{ marginTop: 4, fontSize: 14, color: '#4b5563', whiteSpace: 'pre-line' }}
                  >
                    {toast.description}
                  </p>
                )}
                {toast.orderId && (
                  <p
                    className="text-sm text-gray-700 mt-1"
                    style={{ marginTop: 6, fontSize: 14, color: '#374151' }}
                  >
                    Pedido #<strong>{toast.orderId}</strong> creado con éxito.
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                aria-label="Cerrar"
                className="ml-2 text-gray-400 hover:text-gray-600"
                style={{ marginLeft: 8, color: '#9ca3af', background: 'transparent', border: 0, cursor: 'pointer' }}
              >
                ×
              </button>
            </div>
            {toast.duration > 0 && (
              <div className="mt-3 h-1 w-full bg-gray-100 overflow-hidden rounded"
                   style={{ marginTop: 12, height: 4, width: '100%', background: '#f3f4f6', overflow: 'hidden', borderRadius: 4 }}>
                <div className={`${theme.barClass} h-full`} style={{ height: '100%', backgroundColor: theme.barColor, animation: `bar ${toast.duration}ms linear forwards` }} />
              </div>
            )}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes bar { from { width:100% } to { width:0% } }
        @keyframes fadeIn { from { opacity:0; transform:translateY(-6px);} to { opacity:1; transform:translateY(0);} }
      `}</style>
    </div>
  );
}
/* ----------------------------------------------------------- */

/* ---------------- Overlay de proceso de pedido ----------------- */
function ProcessingOverlay({ visible, text = "Procesando pedido…" }) {
  const { branding } = useBranding();
  const brandColor = branding.colors.primary;
  if (!visible) return null;
  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/10 backdrop-blur-[2px] flex items-center justify-center"
      style={{
        position: 'fixed', inset: 0, zIndex: 9998,
        background: 'rgba(0,0,0,.08)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}
      aria-live="polite"
      aria-busy="true"
      role="status"
    >
      <div
        className="rounded-xl bg-white border border-gray-200 shadow-xl px-6 py-5 flex items-center gap-4"
        style={{
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.08)', padding: '14px 20px',
          display: 'flex', alignItems: 'center', gap: 12
        }}
      >
        <span
          className="animate-spin h-6 w-6 rounded-full border-2 border-gray-300 border-t-brand"
          style={{
            height: 24, width: 24, borderRadius: '9999px',
            border: '2px solid #d1d5db', borderTopColor: brandColor,
            animation: 'spin .9s linear infinite'
          }}
          aria-hidden="true"
        />
        <span className="text-sm font-medium text-gray-700" style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
          {text}
        </span>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
/* --------------------------------------------------------------- */

/** ===================== WRAPPERS DE RUTA ===================== **/

// /:categorySlug y /:categorySlug/:subcategorySlug
function CategoryWrapper({ onAddToCart, onProductClick }) {
  const { categorySlug, subcategorySlug } = useParams();
  return (
    <CategoryPage
      categorySlug={categorySlug}
      subcategorySlug={subcategorySlug}
      onAddToCart={onAddToCart}
      onProductClick={onProductClick}
    />
  );
}

function ProductFromCategoryWrapper({ onAddToCart, onProductClick }) {
  const { productId } = useParams(); // <-- solo jalamos productId

  return (
    <ProductDetailPage
      productId={productId}        // <-- le pasamos el ID numérico correcto
      onAddToCart={onAddToCart}
      onProductClick={onProductClick}
    />
  );
}

// /search y /search/:term
function SearchWrapper({ onAddToCart, onProductClick }) {
  const { term } = useParams(); // puede ser undefined
  const searchTerm = term ? decodeURIComponent(term) : "";
  return (
    <SearchPage
      searchTerm={searchTerm}
      onAddToCart={onAddToCart}
      onProductClick={onProductClick}
    />
  );
}

// /cart
function CartWrapper({
  user,
  cartItems,
  onRemoveFromCart,
  onUpdateQuantity,
  onFinalizePurchase,
  onProductClick,
  onAddToCart,
}) {
  const navigate = useNavigate();
  const handleNavigate = (path) => navigate(path);

  return (
    <CartPage
      user={user}
      onNavigate={handleNavigate}
      cartItems={cartItems}
      onRemoveFromCart={onRemoveFromCart}
      onUpdateQuantity={onUpdateQuantity}
      onFinalizePurchase={onFinalizePurchase}
      onProductClick={onProductClick}
      onAddToCart={onAddToCart}
    />
  );
}

/** ===================== UTILIDAD ===================== **/
const firstWord = (s) => (s || "").trim().split(/\s+/)[0] || "";

/** ===================== APP PRINCIPAL ===================== **/
function App() {
  // ===== ESTADO GLOBAL (IGUAL AL TUYO ANTES) =====
  const { branding } = useBranding();
  const brandColor = branding.colors.primary;
  const accentColor = branding.colors.accent;
  const ogImage = branding.meta.ogImage;
  const iconUrl = branding.logos.icon;

  // Sesión
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('elAnclaUser')); } catch { return null; }
  });

  // Carrito y UI
  const [cartItems, setCartItems] = useState([]);
  const [showAddToCartPopup, setShowAddToCartPopup] = useState(false);
  const [popupProduct, setPopupProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Toast
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const showToast = ({ variant = "info", title, description, duration = 4000, orderId }) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ variant, title, description, duration, orderId });
    if (duration > 0) {
      toastTimerRef.current = setTimeout(() => setToast(null), duration);
    }
  };

  // Overlay de procesamiento
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);

  // Router navigate
  const navigate = useNavigate();
  const location = useLocation();

  const pageMeta = useMemo(
    () => ({
      title: 'El Ancla CR',
      description:
        'Compra perfumes originales, tecnología y productos para el hogar con envío en toda Costa Rica.',
    }),
    []
  );
  const isBlogRoute = (location.pathname || '').startsWith('/blog');

  // AOS (animaciones)
  useEffect(() => {
    AOS.init({ duration: 1000, once: true, offset: 120 });
  }, []);

  // Actualizar canonical con la ruta actual
  useEffect(() => {
    const canonicalHref = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalHref);
  }, [location]);

  // Login
  const handleLoggedIn = (u) => {
    setUser(u);
    localStorage.setItem('elAnclaUser', JSON.stringify(u));
  };

  // Logout
  const handleLogout = () => {
    const firstName = firstWord(user?.nombre);
    setUser(null);
    localStorage.removeItem('elAnclaUser');

    showToast({
      variant: 'info',
      title: 'Sesión cerrada',
      description: firstName ? `¡Hasta luego, ${firstName}!` : 'Has cerrado sesión correctamente.',
      duration: 4000,
    });

    navigate(routes.home);
  };

  // Add to cart
  const handleAddToCart = (productToAdd, options = {}) => {
    const { silent = false } = options;
    setCartItems((prevItems) => {
      const existingItemIndex = prevItems.findIndex((item) => item.id === productToAdd.id);
      if (existingItemIndex > -1) {
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + productToAdd.quantity,
        };
        return updatedItems;
      } else {
        return [...prevItems, { ...productToAdd }];
      }
    });
    if (!silent) {
      setPopupProduct(productToAdd);
      setShowAddToCartPopup(true);
    }
  };

  // Remove from cart
  const handleRemoveFromCart = (productId) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== productId));
  };

  // Update qty
  const handleUpdateQuantity = (productId, newQuantity) => {
    setCartItems((prevItems) =>
      prevItems
        .map((item) => (item.id === productId ? { ...item, quantity: newQuantity } : item))
        .filter(item => item.quantity > 0)
    );
  };

  const sendTelegramNotification = async (orderId, details = {}) => {
    if (!orderId) return;
    try {
      const response = await fetch('/.netlify/functions/notify-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, details }),
      });
      if (!response.ok) {
        const errorPayload = await response.text().catch(() => `HTTP ${response.status}`);
        console.error('Telegram notification failed', errorPayload);
      }
    } catch (err) {
      console.error('Telegram notification failed', err);
    }
  };

  // Finalizar compra (idéntico al tuyo, reemplazamos handleNavigate por navigate)
  const handleFinalizePurchase = async (customerInfo) => {
    setIsProcessingOrder(true);

    const isCardPayment = customerInfo.paymentMethod === 'Pago con tarjeta';
    const orderEmail = (customerInfo.billingEmail || customerInfo.email || user?.correo || '').trim();
    const billingZip = (customerInfo.billingZip || '').trim();
    const shippingZip = (customerInfo.shippingZip || billingZip).trim();

    // Construir tabla de carrito (HTML)
    let total = 0;
    const cartTableHtml = `
      <table style="width:100%; border-collapse: collapse; font-family: Arial, Helvetica, sans-serif; font-size: 14px;">
        <thead>
          <tr style="background-color: #f2f2f2;">
            <th style="padding: 8px; border: 1px solid #ddd; text-align:left;">Cantidad</th>
            <th style="padding: 8px; border: 1px solid #ddd; text-align:left;">Artículo</th>
            <th style="padding: 8px; border: 1px solid #ddd; text-align:left;">Precio Unitario</th>
            <th style="padding: 8px; border: 1px solid #ddd; text-align:right;">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          ${cartItems.map(item => {
            const s = item.price * item.quantity;
            total += s;
            return `
              <tr>
                <td style="padding: 8px; border: 1px solid #ddd;">${item.quantity}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${item.name}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">₡${item.price.toLocaleString('es-CR', { minimumFractionDigits: 2 })}</td>
                <td style="padding: 8px; border: 1px solid #ddd; text-align:right;">₡${s.toLocaleString('es-CR', { minimumFractionDigits: 2 })}</td>
              </tr>`;
          }).join('')}
        </tbody>
      </table>`;
    const totalCR = `₡${total.toLocaleString('es-CR', { minimumFractionDigits: 2 })}`;

    // 1) Guardar orden en Supabase y obtener ID
    let orderId = null;
    try {
      const { data: inserted, error } = await supabase
        .from('orders')
        .insert([{
          name: customerInfo.name,
          phone: customerInfo.phone,
          address: customerInfo.address,
          payment_method: customerInfo.paymentMethod,
          payment_gateway: isCardPayment ? 'tilopay' : 'manual',
          cart: cartItems,
          total,
          cliente_id: user ? user.id : null,
          currency: 'CRC',
          billing_email: orderEmail || null,
          billing_country: customerInfo.billingCountry || 'CR',
          billing_state_iso: customerInfo.billingStateIso || null,
          billing_zip: billingZip || null,
          billing_phone: customerInfo.billingPhone || customerInfo.phone || null,
          shipping_country: customerInfo.shippingCountry || customerInfo.billingCountry || 'CR',
          shipping_state_iso: customerInfo.shippingStateIso || customerInfo.billingStateIso || null,
          shipping_zip: shippingZip || null,
          shipping_phone: customerInfo.shippingPhone || customerInfo.phone || null,
        }])
        .select('id');

      if (error) {
        console.error('Supabase INSERT error:', error);
        showToast({
          variant: 'error',
          title: 'No se pudo guardar el pedido',
          description: error.message || 'Intenta nuevamente.',
        });
        setIsProcessingOrder(false);
        return;
      }

      orderId = Array.isArray(inserted) ? inserted[0]?.id : inserted?.id;
      if (!orderId) {
        console.warn('No se obtuvo el ID del pedido. Data:', inserted);
      }
    } catch (err) {
      console.error('Error inesperado guardando pedido:', err);
      showToast({
        variant: 'error',
        title: 'Error inesperado',
        description: 'Ocurrió un problema al guardar tu pedido.',
      });
      setIsProcessingOrder(false);
      return;
    }

    const orderIdShort = orderId ? String(orderId).split('-')[0] : null;
    const clienteNombre = `${customerInfo.name} ${customerInfo.lastName || ''}`.trim();
    const clienteNombreCorto = firstWord(clienteNombre);
    const clienteCorreo = orderEmail || (customerInfo.correo || '').trim();
    const telegramPayload = {
      id: orderId,
      orderShortId: orderIdShort,
      total,
      cartItems,
      customerName: clienteNombre,
      customerFirstName: clienteNombreCorto,
      phone: customerInfo.phone,
      email: clienteCorreo || null,
      address: customerInfo.address,
      paymentMethod: customerInfo.paymentMethod,
      paymentStatus: isCardPayment ? 'pendiente (tarjeta)' : 'pendiente',
      notes: customerInfo.notes || '',
    };
    sendTelegramNotification(orderId, telegramPayload);

    // 2) Emails
    try {
      // Admin
      await sendUniversalEmail({
        to_email: 'info.elanclacr@gmail.com',
        customer_name: 'Equipo El Ancla',
        title: 'Nuevo pedido recibido',
        preheader: 'Hay un pedido nuevo listo para gestionar.',
        intro_html: `
          <p style="margin:0 0 8px 0;color:#374151;">
            <strong>Pedido #${orderIdShort || orderId || '-'}</strong>
          </p>
          <p style="margin:0 0 8px 0;color:#374151;">
            <strong>Cliente:</strong> ${clienteNombre}<br/>
            <strong>Teléfono:</strong> ${customerInfo.phone}<br/>
            <strong>Dirección:</strong> ${customerInfo.address}<br/>
            <strong>Método de pago:</strong> ${customerInfo.paymentMethod}
          </p>
        `,
        body_html: `
          <h3 style="color:${brandColor};font-size:16px;margin:16px 0 8px 0;">🛍 Productos del pedido</h3>
          <div>${cartTableHtml}</div>
          <div style="margin-top:16px;text-align:right;">
            <span style="font-weight:bold;font-size:16px;">Total del pedido: </span>
            <span style="color:${accentColor};font-size:18px;font-weight:bold;">${totalCR}</span>
          </div>
        `,
        cta_label: 'Abrir pedidos',
        cta_url: `${window.location.origin}${routes.misPedidos}`,
        alt_link_html: '',
        telegram_payload: telegramPayload,
      }, branding);

      // Cliente
      if (clienteCorreo) {
        await sendUniversalEmail({
          to_email: clienteCorreo,
          customer_name: clienteNombreCorto || 'Cliente',
          title: '¡Gracias por tu compra!',
          preheader: 'Recibimos tu pedido. Te contactaremos para coordinar entrega/pago.',
          intro_html: `
            <p style="margin:0 0 8px 0;color:#374151;">
              Tu <strong>pedido #${orderIdShort || orderId || '-'}</strong> fue recibido correctamente.
            </p>
            <p style="margin:0 0 8px 0;color:#374151;">
              Te contactaremos para coordinar entrega/pago. Abajo el resumen de tu compra:
            </p>
          `,
          body_html: `
            <div>${cartTableHtml}</div>
            <div style="margin-top:16px;text-align:right;">
              <span style="font-weight:bold;font-size:16px;">Total del pedido: </span>
              <span style="color:${accentColor};font-size:18px;font-weight:bold;">${totalCR}</span>
            </div>
          `,
          cta_label: 'Ver mis pedidos',
          cta_url: `${window.location.origin}${routes.misPedidos}`,
          alt_link_html: `
            <p style="margin:0 0 18px 0;font-size:13px;color:#6b7280;">
              Si el botón no funciona, abrí este enlace:<br/>
              <a href="${window.location.origin}${routes.misPedidos}" style="color:${brandColor};">${window.location.origin}${routes.misPedidos}</a>
            </p>
          `,
        }, branding);
      } else {
        console.warn("No se encontró correo del cliente para enviar confirmación.");
      }
    } catch (mailErr) {
      console.error('Mail error (post-orden):', mailErr);
      showToast({
        variant: "warning",
        title: "Pedido creado",
        description: "Hubo un problema enviando el correo. Te contactaremos igual.",
      });
    }

    if (isCardPayment) {
      try {
        const response = await fetch('/.netlify/functions/tilopay-process', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orderId,
            redirectUrl: `${window.location.origin}${routes.tilopayReturn}`,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'No pudimos iniciar el pago con tarjeta.');
        }
        setCartItems([]);
        setIsProcessingOrder(false);
        window.location.href = data.url;
        return;
      } catch (err) {
        console.error('Tilopay process error:', err);
        showToast({
          variant: 'error',
          title: 'Pago con tarjeta',
          description: err.message || 'No pudimos iniciar el pago con tarjeta.',
        });
        setIsProcessingOrder(false);
        return;
      }
    }

    // 3) Reset + ir a Mis pedidos + toast
    setCartItems([]);
    setIsProcessingOrder(false);
    const toastDescription = customerInfo.paymentMethod === 'Emma Pay'
      ? 'Te contactaremos para coordinar el cargo por Emma Pay.'
      : 'Te contactaremos para coordinar entrega/pago.';

    showToast({
      variant: "success",
      title: "¡Pedido creado!",
      description: toastDescription,
      orderId: orderIdShort || undefined,
    });

    navigate(routes.misPedidos);
  };

  // Búsqueda desde Header
  const handleSearch = (term) => {
    setSearchTerm(term);
    const cleaned = (term || '').trim();
    if (cleaned) {
      navigate(`${routes.search}/${encodeURIComponent(cleaned)}`);
    } else {
      navigate(routes.search);
    }
  };

const handleProductClick = (productId, basePath) => {
  if (basePath && basePath.trim() !== "") {
    navigate(`${basePath}/${productId}`);
  } else {
   
    navigate(`/tecnologia/${productId}`);
  }
};


  const handleRepeatOrder = (items = [], order = null) => {
    if (!items.length) return;
    items.forEach((item) => {
      if (!item?.id) return;
      const qty = Number(item.quantity) || 1;
      handleAddToCart({ ...item, quantity: qty }, { silent: true });
    });
    const orderLabel = order?.id
      ? `#${String(order.id).includes('-') ? String(order.id).slice(0, 8) : String(order.id).slice(-6)}`
      : '';
    showToast({
      variant: 'success',
      title: 'Productos agregados al carrito',
      description: orderLabel
        ? `Repetimos tu pedido ${orderLabel}. Revisa el carrito para finalizar.`
        : 'Repetimos tu pedido. Revisa el carrito para finalizar.',
    });
  };

  return (
    <>
      {!isBlogRoute && (
        <Helmet>
          <title>{pageMeta.title}</title>
          <meta name="description" content={pageMeta.description} />
          <link rel="icon" href={iconUrl} />
          <meta property="og:image" content={ogImage} />
          <meta name="twitter:image" content={ogImage} />
        </Helmet>
      )}
      <Header
        onNavigate={(path) => navigate(path)}
        user={user}
        onLogout={handleLogout}
        cartItemCount={cartItems.reduce((sum, item) => sum + item.quantity, 0)}
        onSearch={handleSearch}
      />

      <main className="flex-grow min-h-screen bg-white font-sans antialiased text-gray-800 flex flex-col">
        <Routes>
  {/* Home */}
  <Route
    path="/"
    element={
      <HomePage
        onAddToCart={handleAddToCart}
        onProductClick={handleProductClick}
        onNavigate={(path) => navigate(path)}
      />
    }
  />

  {/* Retorno Tilopay */}
  <Route
    path={routes.tilopayReturn}
    element={<TilopayReturnPage />}
  />

  {/* Blog */}
  <Route
    path={routes.blog}
    element={
      <BlogPage onNavigate={(path) => navigate(path)} />
    }
  />
  <Route
    path={routes.blogPost}
    element={
      <BlogPostPage onNavigate={(path) => navigate(path)} />
    }
  />

  {/*
    1. PRODUCTO con categoría + subcategoría + ID
       /tecnologia/accesorios-tecnologicos/942
  */}
  <Route
    path="/:categorySlug/:subcategorySlug/:productId"
    element={
      <ProductFromCategoryWrapper
        onAddToCart={handleAddToCart}
        onProductClick={handleProductClick}
      />
    }
  />

  {/*
    2. LISTA de categoría + subcategoría
       /perfumes/hombre
       /tecnologia/cargadores
       IMPORTANTE: esto DEBE ir antes de "/:categorySlug/:productId"
       para que "hombre" se trate como subcat, no como ID.
  */}
  <Route
    path="/:categorySlug/:subcategorySlug"
    element={
      <CategoryWrapper
        onAddToCart={handleAddToCart}
        onProductClick={handleProductClick}
      />
    }
  />

  {/*
    3. PRODUCTO con categoría + ID (sin subcategoría)
       /perfumes/744
       /tecnologia/942
       Esta ruta queda después para que /perfumes/hombre
       NO se tome como "productId=hombre".
  */}
  <Route
    path="/:categorySlug/:productId"
    element={
      <ProductFromCategoryWrapper
        onAddToCart={handleAddToCart}
        onProductClick={handleProductClick}
      />
    }
  />

  {/*
    4. LISTA de categoría sola
       /perfumes
       /tecnologia
       /hogar
  */}
  <Route
    path="/:categorySlug"
    element={
      <CategoryWrapper
        onAddToCart={handleAddToCart}
        onProductClick={handleProductClick}
      />
    }
  />

  {/* Búsqueda */}
  <Route
    path="/search"
    element={
      <SearchWrapper
        onAddToCart={handleAddToCart}
        onProductClick={handleProductClick}
      />
    }
  />
  <Route
    path="/search/:term"
    element={
      <SearchWrapper
        onAddToCart={handleAddToCart}
        onProductClick={handleProductClick}
      />
    }
  />

  {/* Carrito */}
  <Route
    path={routes.cart}
    element={
      <CartWrapper
        user={user}
        cartItems={cartItems}
        onRemoveFromCart={handleRemoveFromCart}
        onUpdateQuantity={handleUpdateQuantity}
        onFinalizePurchase={handleFinalizePurchase}
        onProductClick={handleProductClick}
        onAddToCart={handleAddToCart}
      />
    }
  />

  {/* Legales / cuenta / auth */}
  <Route path={routes.privacy} element={<PrivacyPolicyPage />} />
  <Route path={routes.warranty} element={<WarrantyPolicyPage />} />
  <Route
    path={routes.reset}
    element={
      <ResetPassword
        onNavigate={(path) => navigate(path)}
        onLoggedIn={handleLoggedIn}
        onNotify={showToast}
      />
    }
  />
  <Route
    path={routes.registro}
    element={
      <Registro
        onNavigate={(path) => navigate(path)}
        onLoggedIn={handleLoggedIn}
        onNotify={showToast}
      />
    }
  />
  <Route
    path={routes.misPedidos}
    element={
      <MisPedidosPage
        user={user}
        onNavigate={(path) => navigate(path)}
        onRepeatOrder={handleRepeatOrder}
      />
    }
  />
  <Route
    path={routes.miCuenta}
    element={
      <MiCuenta
        user={user}
        onNavigate={(path) => navigate(path)}
      />
    }
  />
  <Route
    path={routes.login}
    element={
      <LoginPage
        onNavigate={(path) => navigate(path)}
        onLoggedIn={handleLoggedIn}
        onNotify={showToast}
      />
    }
  />

  {/* fallback */}
  <Route
    path="*"
    element={
      <HomePage
        onAddToCart={handleAddToCart}
        onProductClick={handleProductClick}
        onNavigate={(path) => navigate(path)}
      />
    }
  />
</Routes>


      </main>

      <Footer onNavigate={(path) => navigate(path)} />

      {showAddToCartPopup && popupProduct && (
        <AddToCartPopup
          product={popupProduct}
          onClose={() => setShowAddToCartPopup(false)}
        />
      )}

      <Toast toast={toast} onClose={() => setToast(null)} />
      <ProcessingOverlay visible={isProcessingOrder} text="Procesando pedido…" />
       <WhatsAppWidget />
    </>
  );
}

export default function AppWithRouter() {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <App />
    </BrowserRouter>
  );
}
